<div class="tprm-dialog-shell tprm-dialog-shell--wide tprm-role-dialog">
  <div class="tprm-dialog-header">
    <div class="tprm-dialog-heading-group">
      <p class="tprm-dialog-eyebrow">Access control</p>
      <h1 mat-dialog-title class="tprm-dialog-title">
        {{ data ? "Edit" : "Create" }} Role
      </h1>
      <p class="tprm-dialog-subtitle">
        Define role identity, ownership context, and the modular permissions that
        control secure platform access.
      </p>
    </div>
    <button
      mat-icon-button
      class="tprm-dialog-close"
      (click)="closeDialog()"
      type="button"
      aria-label="Close role dialog"
    >
      <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
    </button>
  </div>

  <form [formGroup]="roleForm" (ngSubmit)="onSubmit()" class="tprm-dialog-form">
    <div mat-dialog-content class="tprm-dialog-content">
      <section class="tprm-dialog-section">
        <h2 class="tprm-dialog-section-title">Role profile</h2>
        <div class="tprm-dialog-grid">
          <div class="tprm-dialog-field">
            <label class="block mb-1">Role Name<span class="text-red-500">*</span></label>
            <input
              formControlName="name"
              placeholder="Enter role name"
              maxlength="100"
              [readonly]="data?.name === 'Vendor Default'"
              [class.text-gray-400]="data?.name === 'Vendor Default'"
              [class.cursor-not-allowed]="data?.name === 'Vendor Default'"
              [class.pointer-events-none]="data?.name === 'Vendor Default'"
              [class.border-red-500]="roleForm.get('name')?.touched && roleForm.get('name')?.invalid"
              class="w-full px-3 py-2 text-[13px] text-black focus:outline-none"
            />
            <div *ngIf="roleForm.get('name')?.touched && roleForm.get('name')?.hasError('required')" class="text-red-500 text-xs mt-1">Role Name is required.</div>
            <div *ngIf="roleForm.get('name')?.touched && roleForm.get('name')?.hasError('pattern')" class="text-red-500 text-xs mt-1">Role Name cannot contain only spaces.</div>
            <div *ngIf="roleForm.get('name')?.touched && roleForm.get('name')?.hasError('maxlength')" class="text-red-500 text-xs mt-1">Role Name cannot exceed 100 characters.</div>
          </div>

          <div class="tprm-dialog-field">
            <label class="block mb-1">Role Description<span class="text-red-500">*</span></label>
            <input
              formControlName="desc"
              placeholder="Enter description"
              [class.border-red-500]="roleForm.get('desc')?.touched && roleForm.get('desc')?.invalid"
              class="w-full px-3 py-2 text-[13px] text-black focus:outline-none"
            />
            <div *ngIf="roleForm.get('desc')?.touched && roleForm.get('desc')?.hasError('required')" class="text-red-500 text-xs mt-1">Role Description is required.</div>
          </div>

          <div class="tprm-dialog-field">
            <label class="block mb-1">Department Name<span class="text-red-500">*</span></label>
            <input
              formControlName="department"
              placeholder="Enter department name"
              [class.border-red-500]="roleForm.get('department')?.touched && roleForm.get('department')?.invalid"
              class="w-full px-3 py-2 text-[13px] text-black focus:outline-none"
            />
            <div *ngIf="roleForm.get('department')?.touched && roleForm.get('department')?.hasError('required')" class="text-red-500 text-xs mt-1">Department Name is required.</div>
          </div>

          <div class="tprm-dialog-field">
            <label class="block mb-1">User Type<span class="text-red-500">*</span></label>
            <mat-form-field class="w-full" appearance="outline" subscriptSizing="dynamic">
              <mat-select formControlName="userRoleType" placeholder="Select user type">
                <mat-option value="user">User</mat-option>
                <mat-option value="vendor">Vendor</mat-option>
                <mat-option value="reviewer">Reviewer</mat-option>
                <mat-option value="evaluator">Evaluator</mat-option>
              </mat-select>
            </mat-form-field>
            <div *ngIf="roleForm.get('userRoleType')?.touched && roleForm.get('userRoleType')?.hasError('required')" class="text-red-500 text-xs mt-1">Please select a User Type.</div>
          </div>
        </div>
      </section>

      <section class="tprm-dialog-section tprm-permission-panel mb-2">
        <div
          class="tprm-permission-panel__header flex justify-between items-center p-4 cursor-pointer rounded-t-[18px]"
          (click)="isModularPermissionsExpanded = !isModularPermissionsExpanded"
        >
          <div>
            <h2 class="tprm-dialog-section-title mb-1">Modular Permissions</h2>
            <p class="text-[12px] text-gray-500 m-0">
              Select the functional access this role should inherit across modules.
            </p>
          </div>
          <mat-icon class="text-gray-400 transition-transform" [class.rotate-180]="!isModularPermissionsExpanded">expand_more</mat-icon>
        </div>

        <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-5" *ngIf="isModularPermissionsExpanded">
          <div
            *ngFor="let module of roleData"
            class="border border-gray-200 rounded-xl bg-white shadow-sm flex flex-col transition-all overflow-hidden"
          >
            <div class="flex justify-between items-center p-3 bg-gray-50/60">
              <mat-checkbox
                [checked]="isAtLeastOnePermissionEnabled(module)"
                (change)="onModuleCheck(module)"
                class="text-[13px] font-semibold text-gray-800"
              >
                {{ module.label || module.moduleName || module.key }}
              </mat-checkbox>
              <mat-icon
                class="text-gray-400 cursor-pointer transition-transform"
                [class.rotate-180]="module.isExpanded"
                (click)="module.isExpanded = !module.isExpanded"
              >
                expand_more
              </mat-icon>
            </div>

            <div
              class="p-4 pt-3 bg-white"
              *ngIf="module.isExpanded && module.subModules?.length > 0"
            >
              <div class="text-[12px] text-gray-400 mb-3 tracking-wide uppercase font-semibold">
                Functional Permissions
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-2">
                <div *ngFor="let sub of module.subModules">
                  <mat-checkbox
                    [checked]="sub.permissions?.[getEnabledAction(sub)]"
                    (change)="togglePermission(sub, getEnabledAction(sub), module)"
                    [disabled]="sub.disabledPermissions?.includes(getEnabledAction(sub))"
                    class="text-[12px] text-gray-600 font-normal"
                  >
                    {{ getDisplayPermissionName(sub.name) }}
                  </mat-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <fuse-alert
        *ngIf="showAlert"
        class="mt-6"
        [appearance]="'outline'"
        [showIcon]="false"
        [type]="alert.type"
      >
        {{ alert.message }}
      </fuse-alert>
    </div>

    <div mat-dialog-actions class="tprm-dialog-actions tprm-dialog-actions--center">
      <button
        type="button"
        (click)="closeDialog()"
        class="tprm-dialog-button tprm-dialog-button--secondary"
      >
        Go back
      </button>
      <button
        type="submit"
        class="tprm-dialog-button tprm-dialog-button--primary"
      >
        {{ data ? "Update" : "Create" }}
      </button>
    </div>
  </form>
</div>
